import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, MessageCircle, Mic, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { agentService } from "../services/agentService";
import InstallAppButton from "./InstallAppButton";
import AgentMessageContent from "./AgentMessageContent";
import "./AgentChatWidget.css";

const WELCOME = "Hi! I am Prithvi (पृथ्वी). Ask about plants, farm devices, or Wi-Fi — in Hindi or English.";

function hasHindiHint(text = "") {
  const value = String(text || "").trim();
  if (/[\u0900-\u097F]/.test(value)) return true;
  return /\b(hindi|हिंदी|हिन्दी|hindi me|in hindi)\b/i.test(value);
}

function resolveSpeechLang({ inputText = "" } = {}) {
  if (hasHindiHint(inputText)) return "hi-IN";
  return "en-IN";
}

function AgentChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [pendingImage, setPendingImage] = useState(null);
  const listRef = useRef(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceFinalRef = useRef("");
  const sendLockRef = useRef(false);
  const pendingImageRef = useRef(null);

  useEffect(() => {
    pendingImageRef.current = pendingImage;
  }, [pendingImage]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, listening, voiceTranscript, pendingImage]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
      }
      if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    };
  }, [pendingImage]);

  function appendAssistant(content, extra = {}) {
    setMessages((prev) => [...prev, { role: "assistant", content, ...extra }]);
  }

  const sendTextMessage = useCallback(async (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed || sendLockRef.current) return;

    sendLockRef.current = true;
    setError("");
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setSending(true);

    try {
      const data = await agentService.sendMessage(trimmed);
      appendAssistant(data.reply, {
        conversationId: data.conversationId,
        feedbackSent: false,
        variant: data.replyStyle || "default",
      });
    } catch (err) {
      const serverMsg = String(err.message || "");
      const modelRetired = /gemini-1\.5/i.test(serverMsg);
      setError(
        modelRetired
          ? "Assistant model needs an update on the server."
          : serverMsg || "Unable to reach the assistant right now."
      );
      appendAssistant(
        err.code === "AGENT_NOT_CONFIGURED"
          ? "The assistant is not configured on the server yet. Please try again later."
          : "Sorry, I could not answer that right now. Please retry in a moment."
      );
    } finally {
      setSending(false);
      sendLockRef.current = false;
    }
  }, []);

  const sendImageMessage = useCallback(async (file, question = "", previewUrl = "") => {
    if (!file || sendLockRef.current) return;

    sendLockRef.current = true;
    setError("");
    const label = question.trim() || "Analyze this plant";
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: label,
        imageUrl: previewUrl || URL.createObjectURL(file),
      },
    ]);
    setSending(true);

    try {
      const data = await agentService.analyzeCropPhoto(file, { question: label });
      appendAssistant(data.reply, {
        conversationId: data.conversationId,
        feedbackSent: false,
        variant: data.replyStyle || (data.mode === "crop_vision" ? "crop_vision" : "default"),
      });
    } catch (err) {
      setError(err.message || "Could not analyze photo.");
      appendAssistant(
        "Sorry, I could not analyze that photo. Use a clear real photo of the plant in daylight (not AI art). Try camera or gallery again."
      );
    } finally {
      setSending(false);
      sendLockRef.current = false;
    }
  }, []);

  async function handleSend(event) {
    event.preventDefault();
    if (sending) return;

    const text = input.trim();
    const attachment = pendingImage;

    if (attachment?.file) {
      const preview = attachment.previewUrl;
      setPendingImage(null);
      setInput("");
      await sendImageMessage(attachment.file, text || "What plant is this and how do I care for it?", preview);
      return;
    }

    if (!text) return;
    setInput("");
    await sendTextMessage(text);
  }

  function attachImageFile(file) {
    if (!file || !file.type.startsWith("image/")) {
      setError("Please choose a photo (JPEG, PNG, or WebP).");
      return;
    }
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage({
      file,
      previewUrl: URL.createObjectURL(file),
    });
    setError("");
  }

  function handleFilePick(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) attachImageFile(file);
  }

  function clearPendingImage() {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  }

  function handlePaste(event) {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          attachImageFile(file);
        }
        break;
      }
    }
  }

  function stopListening() {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // ignore
      }
    }
    setListening(false);
  }

  function startVoice() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Voice is not supported in this browser. Try Chrome or Edge on Android.");
      return;
    }
    if (listening || sending) return;

    setError("");
    setVoiceTranscript("");
    voiceFinalRef.current = "";

    const recognition = new SpeechRecognition();
    recognition.lang = resolveSpeechLang({ inputText: input });
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setListening(true);
      setVoiceTranscript("");
    };

    recognition.onresult = (event) => {
      let interim = "";
      let finalText = voiceFinalRef.current;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const part = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) finalText += part;
        else interim += part;
      }
      voiceFinalRef.current = finalText;
      setVoiceTranscript((finalText || interim).trim());
    };

    recognition.onerror = (event) => {
      setListening(false);
      if (event.error !== "aborted") {
        setError("Could not hear you. Allow microphone access and try again.");
      }
    };

    recognition.onend = async () => {
      setListening(false);
      const text = voiceFinalRef.current.trim();
      setVoiceTranscript("");
      voiceFinalRef.current = "";
      if (!text) return;

      if (pendingImageRef.current?.file) {
        setInput(text);
        setError("");
        return;
      }

      await sendTextMessage(text);
    };

    try {
      recognition.start();
    } catch {
      setError("Microphone could not start. Try again.");
      setListening(false);
    }
  }

  async function handleFeedback(messageIndex, helpful) {
    const message = messages[messageIndex];
    if (!message?.conversationId || message.feedbackSent) return;
    try {
      await agentService.sendFeedback({
        conversationId: message.conversationId,
        helpful,
      });
      setMessages((prev) =>
        prev.map((m, i) => (i === messageIndex ? { ...m, feedbackSent: true, helpful } : m))
      );
    } catch {
      setError("Could not save feedback. Please try again.");
    }
  }

  const showComposer = !listening;

  return (
    <div className="agent-chat-root" aria-live="polite">
      <div className="agent-chat-stack">
      {open ? (
        <section className="agent-chat-panel" aria-label="Prithvi farming assistant">
          <header className="agent-chat-header">
            <div>
              <p className="agent-chat-title">Prithvi <span className="agent-chat-title-hindi">पृथ्वी</span></p>
              <p className="agent-chat-subtitle">Plant care · devices · Wi-Fi help</p>
            </div>
            <button type="button" className="agent-chat-icon-btn" onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={18} />
            </button>
          </header>

          <div className="agent-chat-messages" ref={listRef}>
            {messages.map((msg, index) => (
              <div key={`${index}-${msg.role}`} className={`agent-chat-bubble ${msg.role}`}>
                {msg.imageUrl ? (
                  <img src={msg.imageUrl} alt="Plant you shared" className="agent-chat-msg-image" />
                ) : null}
                {msg.content ? (
                  msg.role === "user" ? (
                    <p className="agent-chat-user-text">{msg.content}</p>
                  ) : (
                    <AgentMessageContent content={msg.content} variant={msg.variant || "default"} />
                  )
                ) : null}
                {msg.role === "assistant" && msg.conversationId ? (
                  <div className="agent-chat-feedback">
                    <button
                      type="button"
                      className={`agent-chat-feedback-btn ${msg.helpful === true ? "active" : ""}`}
                      disabled={msg.feedbackSent}
                      onClick={() => handleFeedback(index, true)}
                      aria-label="Helpful"
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      type="button"
                      className={`agent-chat-feedback-btn ${msg.helpful === false ? "active" : ""}`}
                      disabled={msg.feedbackSent}
                      onClick={() => handleFeedback(index, false)}
                      aria-label="Not helpful"
                    >
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {sending ? <p className="agent-chat-typing">Analyzing...</p> : null}
          </div>

          {error ? <p className="agent-chat-error">{error}</p> : null}

          {listening ? (
            <div className="agent-chat-listening" role="status" aria-live="polite">
              <div className="agent-chat-listening-waves" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <div className="agent-chat-listening-mic" aria-hidden="true">
                <Mic size={28} />
              </div>
              <p className="agent-chat-listening-title">Listening...</p>
              <p className="agent-chat-listening-hint">
                {voiceTranscript ||
                  (pendingImage
                    ? "Speak your question — it will be added with your photo. Tap Send when ready."
                    : "Speak now — I'll reply when you finish")}
              </p>
              <button type="button" className="agent-chat-listening-cancel" onClick={stopListening}>
                Cancel
              </button>
            </div>
          ) : null}

          {showComposer ? (
            <form className="agent-chat-form" onSubmit={handleSend}>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="agent-chat-file-input"
                onChange={handleFilePick}
                aria-hidden
                tabIndex={-1}
              />
              <input
                ref={galleryRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                className="agent-chat-file-input"
                onChange={handleFilePick}
                aria-hidden
                tabIndex={-1}
              />

              <div className="agent-chat-composer">
                {pendingImage ? (
                  <div className="agent-chat-attachment">
                    <img src={pendingImage.previewUrl} alt="Attached plant" />
                    <button type="button" className="agent-chat-attachment-remove" onClick={clearPendingImage} aria-label="Remove image">
                      <X size={14} />
                    </button>
                  </div>
                ) : null}

                <div className="agent-chat-composer-row">
                  <button
                    type="button"
                    className="agent-chat-tool-btn"
                    onClick={() => cameraRef.current?.click()}
                    disabled={sending}
                    aria-label="Take photo with camera"
                    title="Camera"
                  >
                    <Camera size={16} />
                  </button>
                  <button
                    type="button"
                    className="agent-chat-tool-btn"
                    onClick={() => galleryRef.current?.click()}
                    disabled={sending}
                    aria-label="Choose photo from gallery"
                    title="Gallery"
                  >
                    <ImagePlus size={16} />
                  </button>
                  <button
                    type="button"
                    className="agent-chat-tool-btn"
                    onClick={startVoice}
                    disabled={sending}
                    aria-label="Speak your question"
                    title="Voice"
                  >
                    <Mic size={16} />
                  </button>
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPaste={handlePaste}
                    placeholder={
                      pendingImage
                        ? "Type or speak your question, then tap Send…"
                        : "Ask in Hindi or English…"
                    }
                    maxLength={2000}
                    disabled={sending}
                    aria-label="Message"
                    className="agent-chat-textarea"
                  />
                  <button
                    type="submit"
                    className="agent-chat-send"
                    disabled={sending || (!input.trim() && !pendingImage)}
                    aria-label="Send"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </form>
          ) : null}
        </section>
      ) : null}

      <InstallAppButton />
      <button
        type="button"
        className="agent-chat-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close Prithvi assistant" : "Open Prithvi assistant"}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      </div>
    </div>
  );
}

export default AgentChatWidget;
